import { IQsrOrderNotification } from '@/views/QsrOrders/types';
import { MenuCategoryResponse, MenuProductResponse } from '@/services/menu/types';
import {
    IOrderConfig,
    IRestaurant,
    OrderPriceRoundingModeEnum,
    QsrCardViewIndicatorEnum,
    QSRPrintLayoutEnum,
    QSRPrintSizeEnum,
} from '@/views/OrdersTableView/types';
import { EpsonPrinterService } from '@/components/PosPrinteCenter/Printers/Epson';
import { useTranslation } from '@/hooks/translations';
import { OrderRows } from '@/components/PosPrinteCenter/Layouts/Common';
import { useCallback } from 'react';

import styles from '../index.module.scss';

interface IProps {
    vendor: IRestaurant | null;
    order: IQsrOrderNotification;
    productMap: { [key: string]: MenuProductResponse };
    categoryMap: { [key: string]: MenuCategoryResponse };
    currencyCode: string;
    currencySymbol: string;
    roundingMode?: OrderPriceRoundingModeEnum;
    itemSize?: QSRPrintSizeEnum;
    encoding?: string;
    lang: string;
    config?: IOrderConfig;
    onDone: (order: IQsrOrderNotification, layout?: QSRPrintLayoutEnum) => void;
}

export const LayoutCashier = ({
    vendor,
    order,
    productMap,
    categoryMap,
    currencyCode,
    currencySymbol,
    roundingMode,
    itemSize,
    encoding,
    lang,
    config,
    onDone,
}: IProps) => {
    const { t } = useTranslation('common');

    const canDisplay = useCallback(
        (item: QsrCardViewIndicatorEnum) => {
            return (
                !vendor?.order_config?.vendorQsrCardViewHiddenIndicators?.length ||
                !vendor?.order_config?.vendorQsrCardViewHiddenIndicators?.includes(item)
            );
        },
        [vendor],
    );

    const refHandler = (ref: any | undefined) => {
        if (!ref) {
            return;
        }

        EpsonPrinterService.getInstance()
            .print(ref)
            .then(() => {
                onDone?.(order, QSRPrintLayoutEnum.Cashier);
            });
    };

    const isPager = order.urlData.tableId?.indexOf('-') === 0;
    return (
        <div style={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, zIndex: 9999, overflow: 'hidden' }}>
            <table
                ref={refHandler}
                className={styles.layout}
                data-id={order.id}
                data-type="cashier"
                style={{ backgroundColor: '#fff' }}
                data-size={itemSize}
                data-currency={currencySymbol.length > 1 ? 'large' : 'normal'}
                data-encoding={encoding}
            >
                {order.ticketId && canDisplay(QsrCardViewIndicatorEnum.Ticket) && (
                    <tr data-mode="header" data-align="center" data-size={itemSize}>
                        <td colSpan={3}>{t('Ticket: {{id}}', { id: order.ticketId })}</td>
                    </tr>
                )}
                {((isPager && canDisplay(QsrCardViewIndicatorEnum.Pager)) ||
                    (!isPager && canDisplay(QsrCardViewIndicatorEnum.Table))) && (
                    <tr data-mode="header">
                        <td colSpan={3}>
                            {isPager
                                ? t('Pager: {{id}}', { id: order.urlData.name || order.urlData.tableId.slice(1) })
                                : t('Table: {{id}}', { id: order.urlData.name || order.urlData.tableId })}
                        </td>
                    </tr>
                )}
                {order.userInfo?.name && (
                    <tr data-mode="normal">
                        <td colSpan={3}>{t('Customer Name: {{name}}', { name: order.userInfo?.name || '' })}</td>
                    </tr>
                )}
                {order.date && (
                    <tr data-mode="header">
                        <td colSpan={3}>
                            {config?.printerImageBased
                                ? `${t('Date:')} ${order.date}`
                                : t('Date: {{date}}', { date: order.date })}
                        </td>
                    </tr>
                )}
                <tr data-mode="divider">
                    <td colSpan={3} />
                </tr>
                <OrderRows
                    items={order.orderData.items}
                    productMap={productMap}
                    categoryMap={categoryMap}
                    currencyCode={currencyCode}
                    currencySymbol={currencySymbol}
                    roundingMode={roundingMode}
                    indent={0}
                    hideIndex
                    xQty
                    size={itemSize}
                    lang={lang}
                    config={config}
                    hideItemNote
                />
                <tr data-mode="divider">
                    <td colSpan={3} />
                </tr>
                {order.subTotalStr && (
                    <tr data-mode="normal">
                        <td colSpan={2}>{t('Subtotal:')}</td>
                        <td align="right">{order.subTotalStr}</td>
                    </tr>
                )}
                {order.additives?.map((item) => {
                    return (
                        <tr data-mode="normal">
                            <td colSpan={2}>{item.title}</td>
                            <td align="right">{`${currencySymbol} ${item.value}`}</td>
                        </tr>
                    );
                })}
                <tr data-mode="normal">
                    <td colSpan={2}>{t('Total:')}</td>
                    <td align="right">{order.totalStr}</td>
                </tr>
            </table>
        </div>
    );
};
